import { useMemo, useState } from "react"

import {
  ExpandedState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table"

import { createColumns } from "./columns"
import { StudentAssignmentRowViewModel } from "./viewModels/StudentAssignmentRowViewModel"

interface StudentAssignmentsTableProps {
  rows: StudentAssignmentRowViewModel[]
  onAssignmentClick: (row: StudentAssignmentRowViewModel) => void
}

export function StudentAssignmentsTable({
  rows,
  onAssignmentClick,
}: StudentAssignmentsTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [sorting, setSorting] = useState<SortingState>([])

  const columns = useMemo(
    () => createColumns({ onAssignmentClick }),
    [onAssignmentClick],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { expanded, sorting },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
  })

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className="tw-sticky tw-top-0 tw-bg-gray-50"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              style={{
                borderLeft: row.original.isFolder
                  ? `3px solid ${row.original.color}`
                  : undefined,
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className="tw-h-24 tw-text-center"
            >
              When your teacher assigns you work, it will show up here!
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
